import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi, errorText, imgSrc } from "../../lib/api";

export const CrudTab = ({ name, listPath, basePath, idKey, Form, columns, testPrefix }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get(listPath).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, [listPath]);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    setBusy(true);
    try {
      if (editing?.[idKey]) await adminApi.put(`${basePath}/${editing[idKey]}`, body);
      else await adminApi.post(basePath, body);
      toast.success(`${name} saved`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${name.toLowerCase()}? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`${basePath}/${item[idKey]}`);
      toast.success(`${name} deleted`);
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid={`${testPrefix}-count`}>{items.length} {name.toLowerCase()}s</p>
        <button data-testid={`${testPrefix}-add-btn`} onClick={() => setEditing({})} className="btn-primary"><Plus size={15} /> Add {name}</button>
      </div>
      <ul className="divide-y divide-white/10 rounded-2xl border border-white/10 bg-[#12141C]">
        {items.map((it) => (
          <li key={it[idKey]} data-testid={`${testPrefix}-row-${it[idKey]}`} className="flex items-center gap-4 p-4">
            <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-[#0B0C10]">
              {columns.image(it) ? <img src={imgSrc(columns.image(it))} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-white">{columns.title(it)}</p>
              <p className="truncate text-sm text-zinc-400">{columns.sub(it)}</p>
            </div>
            <button aria-label="Edit" data-testid={`${testPrefix}-edit-${it[idKey]}`} onClick={() => setEditing(it)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-zinc-200 hover:border-[#9D4EDD]"><Pencil size={15} /></button>
            <button aria-label="Delete" data-testid={`${testPrefix}-delete-${it[idKey]}`} onClick={() => remove(it)} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-zinc-200 hover:border-red-400 hover:text-red-400"><Trash2 size={15} /></button>
          </li>
        ))}
        {!items.length && <li className="p-8 text-center text-zinc-400">Nothing here yet.</li>}
      </ul>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-lenis-prevent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#12141C] text-white" data-testid={`${testPrefix}-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-3xl font-extrabold uppercase">{editing?.[idKey] ? "Edit" : "Add"} {name}</DialogTitle><DialogDescription className="text-zinc-400">Changes appear on the website as soon as you save.</DialogDescription></DialogHeader>
          {editing && <Form key={editing[idKey] || "new"} initial={editing} onSubmit={save} busy={busy} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
